import TreeView16 from '@carbon/icons-react/es/tree-view/16';
import { Tooltip } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { ifNotProp, ifProp } from 'styled-tools';
import { ProjectId, TaskId } from 'api/models';
import {
  FieldOption,
  FieldValue,
  FieldValueType,
  StatusGroup,
  TaskType,
} from 'api/tasks';
import { IconShell } from 'components/atoms/icon-shell';
import { StatusIndicator } from 'components/molecules/status-indicator';
import {
  RbacStatusPicker,
  RbacStatusPickerTrigger,
} from 'components/organisms/rbac/components/rbac-status-picker';
import { TaskName as RTETaskName } from 'components/organisms/templates/commons/name-field';
import { ClassNameProps } from 'components/ozone-component';
import { ProductiveHeading06 } from 'components/styles';
import {
  useSelectedStatusOption,
  useStatusField,
} from 'data/fields/hooks/account-fields';
import { useProjectDetail } from 'data/projects';
import { updateTaskSagaAction } from 'data/tasks/actions';
import { ProjectResource } from 'hooks/rbac/useWithAccess';

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const SubtaskIcon = styled(Container)`
  margin-right: var(--spacing-03);
  > svg {
    color: var(--gray-70);
  }
`;

const TaskName = styled.div<{
  readOnly?: boolean;
  size?: SizeType;
  isSubTask?: boolean;
}>`
  margin-left: ${ifProp({ size: 'small' }, 'var(--spacing-03)', 0)};
  cursor: ${ifNotProp('readOnly', 'pointer', 'initial')};
  padding: var(--spacing-03) 0;
  width: ${({ isSubTask }) =>
    isSubTask ? 'calc(100% - var(--spacing-10))' : '100%'};
`;

const InfoIcons = styled.div`
  display: flex;
`;

const AtRisk = styled.span`
  color: var(--error-color);
  ${ProductiveHeading06};
  width: 8px;
  margin-left: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface Props extends ClassNameProps {
  attachTo?: HTMLElement | null;
  taskName: string;
  taskId: TaskId;
  fields: FieldValue<FieldValueType>[];
  size?: SizeType;
  onTaskNameClick?: (taskId: TaskId) => void;
  atRisk?: boolean;
  disableStatusDropdown?: boolean;
  progress: number;
  taskType?: TaskType;
  readOnly?: boolean;
  isSubTask?: boolean;
  disableStatusTrigger?: boolean;
  projectId: number;
}

export const TaskNameCell: FC<Props> = (props) => {
  const {
    attachTo,
    className,
    taskName,
    taskId,
    fields,
    size = 'middle',
    onTaskNameClick,
    disableStatusDropdown,
    progress,
    atRisk,
    taskType = TaskType.Task,
    readOnly,
    isSubTask,
    disableStatusTrigger,
    projectId,
  } = props;

  const dispatch = useDispatch();

  const selectedStatusOption = useSelectedStatusOption(fields || []);
  const statusField = useStatusField();
  const taskStatusValue = fields?.find(
    (f) => f.fieldId === statusField?.fieldId,
  );

  const handleStatusChange = (selected: FieldOption<StatusGroup>) => {
    if (statusField) {
      dispatch(
        updateTaskSagaAction({
          projectId: taskId[1],
          taskId: taskId[2],
          data: {
            fields: [
              {
                fieldId: statusField.fieldId,
                fieldValue: selected.value,
              },
            ],
          },
        }),
      );
    }
  };

  const { data: projectDetail } = useProjectDetail(ProjectId(projectId));

  const projectResource = useMemo(
    () => (projectDetail ? ProjectResource(projectDetail) : undefined),
    [projectDetail],
  );

  return (
    <Container className={className}>
      <InfoIcons data-email-template-ignore>
        {atRisk && (
          <Tooltip title={<FormattedMessage id="task.at_risk" />}>
            <AtRisk>!</AtRisk>
          </Tooltip>
        )}
        {selectedStatusOption && taskStatusValue && !disableStatusDropdown && (
          <RbacStatusPicker
            attachTo={attachTo}
            onChange={handleStatusChange}
            field={statusField}
            value={selectedStatusOption.value}
            dropdownHeader={<FormattedMessage id="task_status" />}
            type={taskType}
          >
            <RbacStatusPickerTrigger
              size={size}
              shape="round"
              selectedStatus={selectedStatusOption}
              progress={progress}
              disabled={disableStatusTrigger}
              rule="TASK_STATUS:WRITE"
              resourceData={projectResource}
              propsIfNoAccess={{
                disabled: true,
              }}
            />
          </RbacStatusPicker>
        )}
        {disableStatusDropdown && selectedStatusOption && (
          <IconShell
            icon={
              <StatusIndicator status={selectedStatusOption} type={taskType} />
            }
            size={size}
          />
        )}
      </InfoIcons>
      {isSubTask && (
        <SubtaskIcon>
          <TreeView16 />
        </SubtaskIcon>
      )}
      <TaskName
        readOnly={readOnly}
        size={size}
        onClick={() => onTaskNameClick && onTaskNameClick(taskId)}
        title={taskName}
        isSubTask={isSubTask}
        data-cy="tasks.section-task.name"
      >
        <RTETaskName taskName={taskName} />
      </TaskName>
    </Container>
  );
};
